import { ArrowLeft } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";

import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import homeService from "../../services/HomeService";
import { useQuery } from "@tanstack/react-query";

const SugarLoadDetail = () => {
  const navigate = useNavigate();

  // Use React Query to fetch and cache home page data
  const { data: homeData, isLoading: loading } = useQuery({
    queryKey: ["homePageData"],
    queryFn: () => homeService.getHomePageData(),
  });

  // Get sugar load data from API
  const insightData = homeData?.["Health Insights"]?.sugar_ratio_daily;

  const ratio = insightData?.value ?? 0;
  const status = insightData?.label ?? "Loading...";
  const label = "Sugar Load";

  // Calculate progress percentage (higher ratio = worse, more fill)
  // 0.10 or less = 50% fill (good), 0.20+ = 100% fill (bad)
  const getProgressPercentage = (ratio: number) => {
    if (ratio <= 0) {
      return 0; // No data = empty bar
    }
    if (ratio <= 0.1) {
      return 50; // Good range = half fill
    }
    if (ratio >= 0.2) {
      return 100; // Bad range = full fill
    }
    // Between 0.10-0.20 = 50% to 100%
    return 50 + ((ratio - 0.1) / 0.1) * 50;
  };

  // Get status color
  const getStatusColor = (status: string) => {
    if (status === "Good" || status === "Excellent") {
      return "text-green-600";
    }
    if (status === "Moderate") {
      return "text-orange-600";
    }
    if (status === "Needs Improvement" || status === "Poor") {
      return "text-red-600";
    }
    return "text-muted-foreground";
  };

  // Get progress bar color based on status
  const getProgressColor = (status: string) => {
    if (status === "Good" || status === "Excellent") {
      return "bg-green-500";
    }
    if (status === "Moderate") {
      return "bg-orange-500";
    }
    if (status === "Needs Improvement" || status === "Poor") {
      return "bg-red-500";
    }
    return "bg-orange-500"; // Default
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.HOME)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            {label || "Sugar Load"}
          </h1>
          <div className="h-10 w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="from-warning/10 to-warning/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Sugar Load
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">
              {ratio}
            </div>
            <p className={`text-sm font-semibold ${getStatusColor(status)}`}>
              {status} (Target: ≤0.10)
            </p>
            <div className="mt-4 h-3 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={`h-full rounded-full transition-all ${getProgressColor(status)}`}
                style={{ width: `${getProgressPercentage(ratio)}%` }}
              />
            </div>
          </div>
        </Card>

        {/* What is Sugar Load */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Sugar Load?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Sugar Load measures the percentage of your total carbohydrate intake
            that comes from sugars. A lower sugar-to-carbs ratio indicates a
            healthier diet with more complex carbohydrates, which provides
            sustained energy and better blood sugar control.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="text-success font-semibold">≤ 0.10</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Moderate</span>
              <span className="text-warning font-semibold">0.10 - 0.20</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Needs Improvement</span>
              <span className="font-semibold text-destructive">Above 0.20</span>
            </div>
          </div>
        </Card>

        {/* Tips to Reduce Sugar Load */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            Tips to Reduce Sugar Load
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Choose whole grains over refined carbohydrates</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Replace sugary drinks with water or unsweetened beverages
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Eat whole fruits instead of fruit juices</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Check labels for hidden sugars in processed foods</span>
            </li>
          </ul>
        </Card>



      </div>
    </div>
  );
};

export default SugarLoadDetail;
